import clsx from 'clsx'
import { useHistory } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'

import ThemesDropdown from './ThemesDropdown'

import s from './BreadCrumbs.module.css'

type Props = {
  companyId: string
  domainId: string
  categoryName: string
  isContentIdeasPage?: boolean
}

export const BreadCrumbs = ({
  categoryName,
  companyId,
  domainId,
  isContentIdeasPage = false,
}: Props) => {
  const history = useHistory()
  const [queryParams, setQueryParams] = useQueryParams({
    step: withDefault(StringParam, undefined),
    theme_id: withDefault(StringParam, undefined),
    category_id: withDefault(StringParam, undefined),
    query: withDefault(StringParam, undefined),
    sug_page: withDefault(NumberParam, 0),
    sug_limit: withDefault(NumberParam, 0),
    sort: withDefault(StringParam, undefined),
    sug_positionLte: withDefault(NumberParam, undefined),
    sug_positionGte: withDefault(NumberParam, undefined),
    sug_exclude_position: withDefault(BooleanParam, false),
    'sug_volume:lte': withDefault(NumberParam, undefined),
    'sug_volume:gte': withDefault(NumberParam, undefined),
    'sug_difficulty:lte': withDefault(NumberParam, undefined),
    'sug_difficulty:gte': withDefault(NumberParam, undefined),
    sug_intent: withDefault(NumberParam, undefined),
    sug_keyword: withDefault(StringParam, undefined),
    sug_topic: withDefault(StringParam, undefined),
  })

  const { step } = queryParams

  const clearFilter = () => {
    setQueryParams(
      {
        sug_exclude_position: undefined,
        sug_intent: undefined,
        sug_keyword: undefined,
        sug_topic: undefined,
        'sug_volume:lte': undefined,
        'sug_volume:gte': undefined,
        'sug_difficulty:lte': undefined,
        'sug_difficulty:gte': undefined,
        sug_positionGte: undefined,
        sug_positionLte: undefined,
      },
      'replaceIn'
    )
  }

  const handleCategoryClick = () => {
    setQueryParams({
      step: 'themes',
      theme_id: undefined,
      sug_limit: undefined,
      sug_page: undefined,
      sort: undefined,
    })
    clearFilter()
  }

  const handleInitialStepClick = () => {
    setQueryParams({
      category_id: undefined,
      theme_id: undefined,
      step: undefined,
      query: undefined,
      sug_limit: undefined,
      sug_page: undefined,
      sort: undefined,
    })
    clearFilter()
  }

  const handleBack = () => {
    if (step === 'themes') {
      handleInitialStepClick()
    } else {
      history.goBack()
    }
  }

  const isSuggestionStep =
    step === 'topic_suggestions' || step === 'keyword_suggestions'
  const isShrunk = !isContentIdeasPage && isSuggestionStep

  return (
    <div className={s.wrapper}>
      {!!step && (
        <>
          <Icon
            icon="lucide:arrow-left"
            onClick={handleBack}
            className={s.backBtn}
          />
          <div
            role="none"
            className={clsx(s.step, s.inActive)}
            onClick={handleInitialStepClick}
          >
            Content Ideas
          </div>
          <div className={s.separator}>/</div>
          <div
            title={categoryName}
            role="none"
            onClick={handleCategoryClick}
            className={clsx(s.step, {
              [s.inActive]: isSuggestionStep,
              [s.shrunk]: isShrunk,
            })}
          >
            {categoryName}
          </div>
        </>
      )}

      {!!step && step !== 'themes' && (
        <>
          <div className={s.separator}>/</div>
          <ThemesDropdown
            companyId={companyId}
            domainId={domainId}
            isContentIdeasPage={isContentIdeasPage}
          />
        </>
      )}
    </div>
  )
}
